/**
 * How many new messages a conversation is holding for the viewer.
 *
 * Bobbie, 2026-08-13: "add a badge count in the message of the number of new
 * messages on any thread". The inbox already knew IF a thread was unread (a
 * boolean off the denormalized thread doc plus the viewer's own read
 * cursor); it never knew how many.
 *
 * Two rules shaped this, both of which this app has been burned by before:
 *
 * 1. Never load every message of every thread to get a number. The count is
 *    only ever fetched for threads the boolean already says are unread, and
 *    only messages NEWER than that viewer's read cursor are read back, so a
 *    school-year-long conversation costs the same as a fresh one.
 * 2. Never show a confident number the data does not support. The fetch is
 *    capped at UNREAD_COUNT_WINDOW documents, so a thread past the cap
 *    reports "at least N" and the badge says so with a trailing plus. No
 *    badge at all renders until a real count has come back; the plain unread
 *    dot holds the row until then.
 *
 * The count itself is inbound only: the viewer's own replies and any recalled
 * message are not new mail. That also keeps the badge from ever contradicting
 * the dot beside it, because both are derived from the same read cursor.
 */

import {
  isReadThrough,
  threadAnsweredByViewer,
  threadInboundActivityMs,
} from './messageInboxStateCore.ts';
import type {
  MessageInboxStateDoc,
  ThreadInboxActivityLike,
} from './messageInboxStateCore.ts';
import { boardNotificationLevel } from './boardNotifications.ts';

/**
 * How many messages after the read cursor are fetched per unread thread.
 * Ten keeps the per-thread cost bounded and matches the notification bell,
 * which has capped its own badge at "9+" since it shipped.
 */
export const UNREAD_COUNT_WINDOW = 10;

/** The largest exact number the badge will print. */
export const UNREAD_BADGE_MAX = 9;

export interface ThreadUnreadCount {
  /** Inbound messages actually counted inside the fetched window. */
  count: number;
  /** True when the window filled up, so the real total may be higher. */
  atLeast: boolean;
}

/** Only the fields the count cares about, so tests need no Firestore. */
export interface UnreadCountMessageLike {
  authorUid?: string | null;
  recalled?: boolean;
}

/**
 * The read cursor to count from, or null when this conversation is not
 * unread for the viewer at all. This is the SAME test the inbox row uses to
 * decide whether to tint itself, so the badge and the dot can never disagree
 * about whether a thread is unread. A returned 0 means "never read", which
 * is a real cursor, so callers must compare against null, not truthiness.
 *
 * Bobbie, 2026-09-16 morning batch item 2, verbatim: "I've marked to get no
 * notifications for the admin board but it still showed up as a message for
 * me. No notification should mean no message either." Every alert channel
 * already honoured her mute (notificationAuthority.mjs's
 * adminBoardNotificationMuted), but this function never took it as an
 * input, so a fully muted admin board thread still bolded in Messages and
 * every home surface built on top of it. `mutedIds` is the viewer's own
 * notifPrefs.mutedIds, and only the level that means "nothing" (the exact
 * stored value boardNotificationLevel already reads, never a re-guessed
 * string) suppresses the unread test - the middle level ("just for
 * original post") still leaves a reply unread, same as before. Membership,
 * lastInboundAtByUid and the read cursor are untouched: she can still open
 * and read the board, only the "new message" signal goes quiet. Scoped to
 * the admin board only, her words - no other board's mute is read here.
 *
 * REFUTE R1 finding 1 (BLOCKER): `thread.adminBoard === true` alone is not
 * the server's own test for a real Admin board thread. The thread-create
 * rule in `shared/firestore.rules` is a blacklist and `adminBoard` is not on
 * it (unlike `dancerStaffThread`, which is, for exactly this reason), so a
 * plain active family account CAN create an ordinary DM carrying a forged
 * `adminBoard: true` - proven in the rules emulator. Before this guard
 * existed that forged bit did nothing; this guard would have turned it into
 * a way for a sender to silence their own DM for a recipient who happens to
 * have the real Admin board muted at nothing. The fix is to require the
 * SAME predicate the server uses,
 * `hub/netlify/functions/lib/adminBoardAccess.mjs`'s
 * `isAdminBoardThread`: `thread.kind === 'group' && thread.adminBoard ===
 * true`. That server file is Netlify Function code (Admin SDK, not meant
 * for the client bundle) so it cannot be imported here; this mirrors it
 * exactly rather than defining a second, drifting definition. A real Admin
 * board thread is always created server side as `{ kind: 'group',
 * adminBoard: true, ... }` (`adminBoard.mjs`), so this changes nothing for
 * the real board. The rules blacklist gap itself is a separate item, filed
 * to Bobbie, and is not fixed here.
 */
export function threadUnreadSinceMs(
  thread: ThreadInboxActivityLike,
  state: Pick<MessageInboxStateDoc, 'readThroughMs'> | null | undefined,
  viewerUid: string,
  mutedIds?: string[] | null
): number | null {
  if (!viewerUid) return null;
  const inboundMs = threadInboundActivityMs(thread, viewerUid);
  if (inboundMs <= 0) return null;
  if (threadAnsweredByViewer(thread, viewerUid)) return null;
  if (isReadThrough(state, inboundMs)) return null;
  if (
    thread.kind === 'group'
    && thread.adminBoard === true
    && boardNotificationLevel(mutedIds ?? undefined, 'adminBoard') === 'nothing'
  ) {
    return null;
  }
  const readThroughMs = state?.readThroughMs;
  return typeof readThroughMs === 'number' && Number.isFinite(readThroughMs)
    ? Math.max(0, Math.floor(readThroughMs))
    : 0;
}

/**
 * Count the inbound messages in one fetched window. `messages` must be
 * everything the query returned, newest first, so a full window is the
 * signal that older unread messages may still exist behind it.
 *
 * `viewerAliasUids` (J32 re-pass finding 4, P3): on a merged DM, a Jr
 * Staff's own OTHER sign-in is still a plain author in the window, so a
 * literal `authorUid === viewerUid` check counts her own send from her
 * email account as unread mail on her dancer sign-in. When given, this
 * excludes EVERY uid in the set rather than only the literal `viewerUid`;
 * omitted (every existing caller before this fix), behaviour is unchanged.
 */
export function countUnreadMessages(
  messages: readonly UnreadCountMessageLike[],
  viewerUid: string,
  windowSize: number = UNREAD_COUNT_WINDOW,
  viewerAliasUids?: ReadonlySet<string> | readonly string[]
): ThreadUnreadCount {
  const mine = viewerAliasUids
    ? (viewerAliasUids instanceof Set ? viewerAliasUids : new Set(viewerAliasUids))
    : null;
  let count = 0;
  for (const message of messages) {
    if (!message) continue;
    if (mine ? mine.has(message.authorUid ?? '') : message.authorUid === viewerUid) continue;
    if (message.recalled === true) continue;
    count += 1;
  }
  return { count, atLeast: messages.length >= windowSize };
}

/**
 * What the badge prints, or null when there is nothing honest to print. A
 * trailing plus is never decoration: it means the real number is at least
 * this, which is all a capped window can prove.
 */
export function unreadBadgeText(unread: ThreadUnreadCount | null | undefined): string | null {
  if (!unread) return null;
  const { count } = unread;
  if (!Number.isFinite(count) || count <= 0) return null;
  if (count > UNREAD_BADGE_MAX) return `${UNREAD_BADGE_MAX}+`;
  return unread.atLeast ? `${count}+` : `${count}`;
}

/** Screen reader wording for the same badge. */
export function unreadBadgeLabel(unread: ThreadUnreadCount | null | undefined): string | null {
  if (!unreadBadgeText(unread) || !unread) return null;
  if (unread.atLeast) return `${unread.count} or more new messages`;
  return unread.count === 1 ? '1 new message' : `${unread.count} new messages`;
}

/**
 * One unread thread's fetch key. Every field that can change the answer is
 * in it, so a cached count is a fact that does not go stale: a new message
 * moves inboundMs, and reading moves readThroughMs.
 *
 * B48: a recall moves neither cursor (the rules let a recall write touch only
 * the thread's lastMessageText), so the count taken before the recall, still
 * holding the recalled message, was served again under the same key and the
 * badge stayed one too high. The preview text is the one signal the thread
 * doc carries: the recall of the newest message flips it to "Message
 * recalled". previewMark is a short fingerprint of that text, so the flip
 * mints a new key and one fresh count. An edit of the newest message also
 * moves it, which costs one bounded refetch and changes nothing else. A
 * recall of an OLDER message leaves the thread doc untouched, so no client
 * signal exists for it without a new listener; that count corrects on the
 * next new message or read.
 */
export interface ThreadUnreadRequest {
  threadId: string;
  /** Newest inbound activity for this viewer, in milliseconds. */
  inboundMs: number;
  /** Everything at or before this is already read. */
  readThroughMs: number;
  /** Fingerprint of the thread's preview text; changes when it is recalled. */
  previewMark?: string;
}

/** A short, stable fingerprint (FNV-1a, base 36). Not security, only change detection. */
function previewFingerprint(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36);
}

/** The request Messages builds for one unread thread, from its live doc. */
export function threadUnreadRequestFor(
  thread: ThreadInboxActivityLike & { id: string; lastMessageText?: string | null },
  viewerUid: string,
  readThroughMs: number
): ThreadUnreadRequest {
  const request: ThreadUnreadRequest = {
    threadId: thread.id,
    inboundMs: threadInboundActivityMs(thread, viewerUid),
    readThroughMs,
  };
  if (typeof thread.lastMessageText === 'string') {
    request.previewMark = previewFingerprint(thread.lastMessageText);
  }
  return request;
}

export function threadUnreadRequestKey(request: ThreadUnreadRequest): string {
  const base = `${request.threadId}|${Math.max(0, Math.floor(request.readThroughMs))}|${Math.max(0, Math.floor(request.inboundMs))}`;
  return request.previewMark ? `${base}|${request.previewMark}` : base;
}

export function parseThreadUnreadRequestKey(key: string): ThreadUnreadRequest | null {
  const parts = key.split('|');
  if (parts.length !== 3 && !(parts.length === 4 && /^[0-9a-z]+$/.test(parts[3]))) return null;
  const [threadId, readThroughRaw, inboundRaw] = parts;
  const readThroughMs = Number(readThroughRaw);
  const inboundMs = Number(inboundRaw);
  if (!threadId || !Number.isFinite(readThroughMs) || !Number.isFinite(inboundMs)) return null;
  const request: ThreadUnreadRequest = { threadId, readThroughMs, inboundMs };
  if (parts.length === 4) request.previewMark = parts[3];
  return request;
}
